import { ReuseModal } from "../ReuseModal";
import { Stack, Typography } from "@mui/material";
import { useState } from "react";
import { ReuseInput } from "../ReuseInput";
import { NAME_REGEX, URL_REGEX } from "../../../utils/regex";
import {
  accountsApi,
  campaignsApi,
  useEditCompanyDataMutation,
} from "../../../api";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { useAppDispatch } from "../../../hooks";

type Errors = { [key: string]: boolean };

const validation: { [key: string]: (value: string) => boolean } = {
  name: (value: string) => !NAME_REGEX.test(value),
  website_url: (value: string) => !!value && !URL_REGEX.test(value),
};

type InitialValues = {
  name: string;
  website_url: string;
};

export const EditAccountDetailsModal = ({
  account_id,
  campaign_id,
  accountName,
  accountWebsite,
  onClose,
}: {
  account_id: string;
  campaign_id: string;
  accountName: string;
  accountWebsite?: string;
  onClose: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState<InitialValues>({
    name: accountName,
    website_url: accountWebsite || "",
  });

  const [error, setError] = useState<Errors>({
    name: false,
    website_url: false,
  });

  const [editCompanyData, { isLoading }] = useEditCompanyDataMutation();

  const inputChangeHandler = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({
      ...prev,
      [field]: validation[field](value),
    }));
  };

  const submitHandler = () => {
    editCompanyData({
      campaign_id,
      account_id,
      name: formData.name,
      website_url: accountWebsite ? undefined : formData.website_url,
    })
      .unwrap()
      .then(() => {
        dispatch(campaignsApi.util.invalidateTags(["company"]));
        dispatch(accountsApi.util.invalidateTags(["list-view"]));
        onClose();
      })
      .catch((err) =>
        toast.error(
          <CustomToast
            type={"error"}
            text={
              getErrorMessage(err).globalError ||
              "Something went wrong. Please try again"
            }
            title={"Something went wrong"}
          />,
        ),
      );
  };

  return (
    <ReuseModal
      isOpen={true}
      onClose={onClose}
      onSubmit={submitHandler}
      title="Edit Account Details"
      maxWidth="540px"
      submitBtnText="Save Changes"
      submitBtnColor="#1C3C6C"
      isDisabled={Object.values(error).some(Boolean) || isLoading}
      isDisabledCancelBtn={isLoading}
      isLoading={isLoading}
    >
      <Stack gap="42px" paddingTop={"8px"} paddingBottom={"24px"}>
        <Typography component="span" variant="body1" color="text.secondary">
          Update the account information to keep records accurate and up to
          date.
        </Typography>
        <Stack gap={"48px"}>
          <ReuseInput
            name="name"
            handleInputChange={inputChangeHandler}
            errorMessage={
              "Must contain only letters, numbers, and basic punctuation"
            }
            value={formData.name || ""}
            label="Account Name"
            placeholder="Account Name"
            isError={error.name}
            setErrorHandler={() => {}}
          />
          <ReuseInput
            name="website_url"
            handleInputChange={inputChangeHandler}
            errorMessage={
              <Stack component="span" alignItems="left" width="100%">
                <span>Invalid URL: Please check the format and try again.</span>
              </Stack>
            }
            value={formData.website_url}
            label="Account Website"
            placeholder="Example: https://www.example.com"
            isError={error.website_url}
            setErrorHandler={() => {}}
            isDisabled={!!accountWebsite}
          />
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
